import { extractApiErrorMessage } from '../../../Utils/apiErrorUtils';
import { BookingSimplifiedCostSchemaMismatchError } from './bookingSimplifiedCostMapper';

export type BookingSimplifiedCostErrorCategory =
  | 'bookingNotFound'
  | 'endpointUnavailable'
  | 'server5xx'
  | 'timeout'
  | 'network'
  | 'schemaMismatch'
  | 'validation';

export interface BookingSimplifiedCostErrorClassification {
  category: BookingSimplifiedCostErrorCategory;
  message: string;
}

const SCHEMA_MISMATCH_MESSAGE =
  'Wykryto niezgodność wersji frontendu i backendu dla quick-create. Skontaktuj się z administratorem.';

const resolveErrorData = (error: unknown): Record<string, unknown> | null => {
  if (!error || typeof error !== 'object' || !('data' in error)) {
    return null;
  }

  const data = (error as { data?: unknown }).data;
  return data && typeof data === 'object' ? (data as Record<string, unknown>) : null;
};

const resolveErrorCode = (error: unknown): string | null => {
  const data = resolveErrorData(error);
  if (!data) {
    return null;
  }

  const rawErrorCode = data.errorCode;
  return typeof rawErrorCode === 'string' ? rawErrorCode : null;
};

const hasValidationErrors = (error: unknown): boolean => {
  const data = resolveErrorData(error);
  if (!data || !('errors' in data)) {
    return false;
  }

  const errors = data.errors;
  return Boolean(errors && typeof errors === 'object');
};

export const isRetryableBookingSimplifiedCostCategory = (
  category: BookingSimplifiedCostErrorCategory
): boolean => category === 'server5xx' || category === 'timeout' || category === 'network';

export const classifyBookingSimplifiedCostError = (
  error: unknown
): BookingSimplifiedCostErrorClassification => {
  if (error instanceof BookingSimplifiedCostSchemaMismatchError) {
    return {
      category: 'schemaMismatch',
      message: SCHEMA_MISMATCH_MESSAGE,
    };
  }

  const status = typeof error === 'object' && error !== null && 'status' in error
    ? (error as { status?: unknown }).status
    : undefined;
  const errorCode = resolveErrorCode(error);

  if (status === 404 && errorCode === 'BOOKING_NOT_FOUND') {
    return {
      category: 'bookingNotFound',
      message: 'Booking nie istnieje lub został usunięty.',
    };
  }

  if (status === 404 || status === 405) {
    return {
      category: 'endpointUnavailable',
      message:
        'Funkcja szybkiego tworzenia jest niedostępna na tej wersji backendu. Użyj flow w sekcji expense-invoices.',
    };
  }

  if (typeof status === 'number' && status >= 500 && status <= 599) {
    return {
      category: 'server5xx',
      message:
        'Serwer chwilowo nie odpowiada poprawnie. Spróbuję automatycznie ponowić zapis jeden raz.',
    };
  }

  if (status === 'TIMEOUT_ERROR') {
    return {
      category: 'timeout',
      message:
        'Przekroczono czas oczekiwania na odpowiedź serwera. Spróbuję automatycznie ponowić zapis jeden raz.',
    };
  }

  if (status === 'FETCH_ERROR') {
    return {
      category: 'network',
      message:
        'Wystąpił błąd sieci podczas zapisu. Spróbuję automatycznie ponowić zapis jeden raz.',
    };
  }

  if (status === 422) {
    if (hasValidationErrors(error)) {
      return {
        category: 'validation',
        message: extractApiErrorMessage(
          error,
          'Nie udało się utworzyć kosztu uproszczonego dla tego bookingu.'
        ),
      };
    }

    return {
      category: 'schemaMismatch',
      message: SCHEMA_MISMATCH_MESSAGE,
    };
  }

  if (status === 'PARSING_ERROR') {
    return {
      category: 'schemaMismatch',
      message: SCHEMA_MISMATCH_MESSAGE,
    };
  }

  return {
    category: 'validation',
    message: extractApiErrorMessage(
      error,
      'Nie udało się utworzyć kosztu uproszczonego dla tego bookingu.'
    ),
  };
};
